/**
 * iOS Safari grants programmatic (no-gesture) audio playback per element, not
 * per page: only an element that has already produced audible output inside a
 * real user gesture may later play unattended, a muted probe does not count,
 * and a fresh `new Audio()` starts locked again. One shared element is
 * therefore primed with a silent-but-audible clip during a user gesture and
 * reused for every unattended notification sound.
 */

// A short clip of silence: audible playback as far as iOS is concerned.
// Served as a bundled asset because the enforced media-src CSP allows only
// same-origin sources, not data: URIs. `no-inline` (not `?url`) is
// load-bearing: a plain `?url` import of an asset under 4 KB is inlined back
// into a data: URI by the production build. MP3 rather than WAV because the
// stock nginx mime map has no wav entry, and iOS refuses to play media served
// as application/octet-stream.
import SILENT_SOUND from '@/sounds/silence.mp3?no-inline';

let sharedElement: HTMLAudioElement | null = null;

export const acquireNotificationAudio = (): HTMLAudioElement => {
    sharedElement ??= new Audio();

    return sharedElement;
};

/**
 * Call this from a click/submit handler, synchronously and before any `await`,
 * so the gesture is still attributable to the playback. Resolves to whether
 * the shared element ended up unlocked.
 */
export const unlockAudioPlayback = (): Promise<boolean> => {
    try {
        const element = acquireNotificationAudio();
        if (!element.paused) {
            return Promise.resolve(true);
        }

        element.muted = false;
        element.src = SILENT_SOUND;

        return element.play().then(() => true, () => false);
    } catch {
        // Unlocking is a best-effort side effect and must never block the caller.
        return Promise.resolve(false);
    }
};

const GESTURE_EVENTS = ['click', 'touchend', 'keydown'] as const;

/**
 * The login submit is not the only way into the app: a session restored from
 * a cookie skips the form entirely, and a reload after login discards the
 * unlocked element. Priming on the first gesture of any kind covers those
 * paths. Returns a teardown that removes the listeners.
 */
export const unlockAudioPlaybackOnFirstGesture = (): (() => void) => {
    const controller = new AbortController();

    const onGesture = (): void => {
        void unlockAudioPlayback().then((unlocked) => {
            if (unlocked) {
                controller.abort();
            }
        });
    };

    GESTURE_EVENTS.forEach((event) => {
        document.addEventListener(event, onGesture, {
            capture: true,
            passive: true,
            signal: controller.signal,
        });
    });

    return () => controller.abort();
};
